import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ReactSVG } from "react-svg";
import { intervalToDuration } from "date-fns";

// molecules
import { TravelDialog, pumpOptions, InactiveUserDialog } from "@molecules/index";

// hooks
import { useDownloadProvider } from "@hooks/useDownloadArchive";

// icons
import {
  Barcode,
  ContractConcreteRecipeStatus,
  InvoiceInstallmentStatus,
  TravelStatusCanceled,
  TravelStatusDelivered,
  TravelStatusDischarging,
  TravelStatusInConstruction,
  TravelStatusInTransit,
  TravelStatusPreparing,
  TravelStatusProgrammed,
  ConcretingRequestConcreteRecipeStatus,
  ConcretingRequestConcretingStatus,
  ConcretingRequestPumpStatus,
  InactivateUser,
  ActivateUser
} from "@assets/icons";

import { permissions } from "@assets/applications/index";

// libraries
import { ArrowForwardIos, Delete } from "@libraries/icons";
import { uuidv4 } from "@libraries/uuid";

// languages
import useTranslation from "@languages/useTranslation";
import { TLanguages } from "@languages/languageData";

// helpers
import { currencyFormat } from "@helpers/index";
import utils from "@helpers/utils";

// types
import {
  ContractConcreteRecipeType,
  TravelType,
  InvoiceInstallmentType,
  ETravelStatus,
  EConcretingRequestPump,
  EConcretingRequestStatus,
  UserType
} from "@store/graphql/helpers/types.graphql";

import { BaseCardMessages, SignUpMessages } from "src/languages/interfaces";
import * as Styled from "./styles";

export interface IConcretingRequestConcretingData {
  status: EConcretingRequestStatus | `${EConcretingRequestStatus}`;
  concreteBatchingPlantName: string;
  requestDate: string;
  // toConfirm: EConcretingRequestToConfirm | `${EConcretingRequestToConfirm}`;
  // travelsReleased?: number;
  observationConcreting?: string;
  interval: string;
  requester: string;
  requesterPhoneNumber: string;
}

export interface IConcretingRequestConcreteRecipeData {
  status: EConcretingRequestStatus | `${EConcretingRequestStatus}`;
  concreteRecipeDescription: string;
  requestedQuantity: number;
  quantityPerConcreteMixer: number;
  pieceToBeConcreted?: string;
}

export interface IConcretingRequestPumpData {
  status: EConcretingRequestStatus | `${EConcretingRequestStatus}`;
  pump: EConcretingRequestPump | `${EConcretingRequestPump}`;
  pipingDistance: number;
  observationPump?: string;
}

export interface IUserData {
  status?: string;
  clientName: string;
  document?: string;
  phoneNumber: string;
}

interface BaseCardItemProps {
  type:
    | "contractConcreteRecipe"
    | "travel"
    | "invoiceInstallment"
    | "concretingRequestConcreting"
    | "concretingRequestPump"
    | "concretingRequestConcreteRecipe"
    | "user";
  contractConcreteRecipe?: ContractConcreteRecipeType;
  travel?: TravelType;
  invoiceInstallment?: InvoiceInstallmentType;
  concretingRequestConcreting?: IConcretingRequestConcretingData;
  concretingRequestConcreteRecipe?: IConcretingRequestConcreteRecipeData;
  concretingRequestPump?: IConcretingRequestPumpData;
  user?: UserType;
  shadowInset?: boolean;
  controlDialog?: (open: boolean) => void;
  concretingId?: string;
  showVolumePrice?: boolean;
}

interface IIcons {
  [key: string]: {
    icon: string;
    color: string;
    labelStatus?: string;
    prefix?: string;
    labelData?: string;
  };
}

interface IBaseCardIcons {
  contractConcreteRecipe: IIcons;
  travel: IIcons;
  invoiceInstallment: IIcons;
  concretingRequestConcreting: IIcons;
  concretingRequestConcreteRecipe: IIcons;
  concretingRequestPump: IIcons;
  user: IIcons;
}

const BaseCardItem: React.FC<BaseCardItemProps> = ({
  type,
  contractConcreteRecipe,
  travel,
  invoiceInstallment,
  concretingRequestConcreting,
  concretingRequestConcreteRecipe,
  concretingRequestPump,
  user,
  shadowInset = true,
  controlDialog,
  concretingId,
  showVolumePrice
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const { t, i18n } = useTranslation();
  const { downloadArchive } = useDownloadProvider();

  const [nowDate, setNowDate] = useState(new Date());
  const cardItemContainerId = useMemo(() => {
    if (type === "travel") return travel?.id;
    if (type === "contractConcreteRecipe") return contractConcreteRecipe?.id;
    if (type === "invoiceInstallment") return invoiceInstallment?.id;
    if (type === "user") return user?.id;

    // generate a random uuid
    return uuidv4();
  }, [type, contractConcreteRecipe, travel, invoiceInstallment]);

  const baseCardIcons: IBaseCardIcons = useMemo(() => ({
    contractConcreteRecipe: {
      ACTIVE: { icon: ContractConcreteRecipeStatus, color: "#64DD17" },
      INACTIVE: { icon: ContractConcreteRecipeStatus, color: "#A3A3A3" }
    },
    travel: {
      PROGRAMMED: {
        icon: TravelStatusProgrammed,
        color: "#64DD17",
        labelStatus: t(BaseCardMessages.statusProgrammedLabelText),
        prefix: t(BaseCardMessages.statusProgrammedPrefixText)
      },
      PREPARING: {
        icon: TravelStatusPreparing,
        color: "#ED7117",
        labelStatus: t(BaseCardMessages.statusPreparingLabelText)
      },
      IN_TRANSIT: {
        icon: TravelStatusInTransit,
        color: "#7E73F8",
        labelStatus: t(BaseCardMessages.statusInTransitLabelText),
        prefix: t(BaseCardMessages.statusInTransitPrefixText)
      },
      IN_CONSTRUCTION: {
        icon: TravelStatusInConstruction,
        color: "#C7B301",
        labelStatus: t(BaseCardMessages.statusInConstructionLabelText),
        prefix: t(BaseCardMessages.statusInConstructionPrefixText),
        labelData: `${t(
          BaseCardMessages.statusInConstructionDescriptionText
        )}:`
      },
      DISCHARGING: {
        icon: TravelStatusDischarging,
        color: "#616161",
        labelStatus: t(BaseCardMessages.statusDischargingLabelText),
        prefix: t(BaseCardMessages.statusDischargingPrefixText),
        labelData: `${t(BaseCardMessages.statusDischargingDescriptionText)}:`
      },
      DELIVERED: {
        icon: TravelStatusDelivered,
        color: "#64DD17",
        labelStatus: t(BaseCardMessages.statusDeliveredLabelText),
        labelData: `${t(BaseCardMessages.statusDeliveredDescriptionText)}:`
      },
      CANCELED: {
        icon: TravelStatusCanceled,
        color: "#FF0000",
        labelStatus: t(BaseCardMessages.statusCanceledLabelText)
      },
      INVOICE_ISSUED: {
        icon: InvoiceInstallmentStatus,
        color: "#64DD17",
        labelStatus: t(BaseCardMessages.statusInvoiceIssuedLabelText),
        prefix: t(BaseCardMessages.statusInvoiceIssuedPrefixText)
      }
    },
    invoiceInstallment: {
      OPEN: { icon: InvoiceInstallmentStatus, color: "#64DD17" },
      OVERDUE: { icon: InvoiceInstallmentStatus, color: "#FF0000" },
      PAID: { icon: InvoiceInstallmentStatus, color: "#A3A3A3" }
    },
    concretingRequestConcreting: {
      APPROVED: { icon: ConcretingRequestConcretingStatus, color: "#64DD17" },
      REJECTED: { icon: ConcretingRequestConcretingStatus, color: "#FF0000" },
      PENDING: { icon: ConcretingRequestConcretingStatus, color: "#A3A3A3" }
    },
    concretingRequestConcreteRecipe: {
      APPROVED: {
        icon: ConcretingRequestConcreteRecipeStatus,
        color: "#64DD17"
      },
      REJECTED: {
        icon: ConcretingRequestConcreteRecipeStatus,
        color: "#FF0000"
      },
      PENDING: {
        icon: ConcretingRequestConcreteRecipeStatus,
        color: "#A3A3A3"
      }
    },
    concretingRequestPump: {
      APPROVED: { icon: ConcretingRequestPumpStatus, color: "#64DD17" },
      REJECTED: { icon: ConcretingRequestPumpStatus, color: "#FF0000" },
      PENDING: { icon: ConcretingRequestPumpStatus, color: "#A3A3A3" }
    },
    user: {
      VALIDATED: { icon: permissions, color: "#64DD17" },
      NOT_VALIDATED: { icon: permissions, color: "#FF0000" }
    }
  } as IBaseCardIcons), [t]);

  // Travel Dialog State
  const [travelDialogOpen, setTravelDialogOpen] = React.useState(false);
  const handleTravelDialog = useCallback(
    (open: boolean) => {
      setTravelDialogOpen(open);
      if (controlDialog) controlDialog(open);
    },
    [controlDialog]
  );

  const hasAction = useMemo(() => {
    if (
      type === "travel"
      && ["DISCHARGING", "IN_CONSTRUCTION", "IN_TRANSIT", "DELIVERED"].includes(
        travel?.status ?? ("" as ETravelStatus)
      )
    ) {
      return true;
    }
    if (type === "invoiceInstallment") return true;

    return false;
  }, [type, travel]);

  const handleClick = useCallback(async () => {
    if (
      type === "travel"
      && ["DISCHARGING", "IN_CONSTRUCTION", "IN_TRANSIT", "DELIVERED"].includes(
        travel?.status ?? ("" as ETravelStatus)
      )
      && !travelDialogOpen
    ) {
      handleTravelDialog(true);
    }

    if (type === "invoiceInstallment") {
      if (!invoiceInstallment?.archive) return;

      await downloadArchive(invoiceInstallment.archive);
    }
  }, [
    travelDialogOpen,
    type,
    handleTravelDialog,
    travel?.status,
    downloadArchive,
    invoiceInstallment
  ]);

  const objectInUse = useMemo(() => {
    if (type === "contractConcreteRecipe") return contractConcreteRecipe as ContractConcreteRecipeType;

    if (type === "travel") return travel as TravelType;

    if (type === "invoiceInstallment") return invoiceInstallment as InvoiceInstallmentType;

    if (type === "concretingRequestConcreteRecipe") return concretingRequestConcreteRecipe as IConcretingRequestConcreteRecipeData;

    if (type === "concretingRequestConcreting") return concretingRequestConcreting as IConcretingRequestConcretingData;

    if (type === "user") {
      const userData = user as IUserData;

      userData.status = user?.isValidated ? "VALIDATED" : "NOT_VALIDATED";

      return userData;
    }

    return concretingRequestPump as IConcretingRequestPumpData;
  }, [
    type,
    contractConcreteRecipe,
    travel,
    invoiceInstallment,
    concretingRequestConcreteRecipe,
    concretingRequestConcreting,
    concretingRequestPump
  ]);

  const contentContractConcreteRecipe = useMemo(() => {

    if (!contractConcreteRecipe) return null;

    const balance = contractConcreteRecipe.totalVolume
      - (contractConcreteRecipe.deliveredVolume || 0);
    const m3ValueFormatted = currencyFormat(
      contractConcreteRecipe.m3Value,
      i18n.language as TLanguages
    );

    return (
      <div>
        <div>{contractConcreteRecipe.concreteRecipe?.description}</div>

        <div>
          <span className="titleContent">
            {`${t(BaseCardMessages.volumeTotalText)}`}:{" "}
          </span>
          {contractConcreteRecipe.totalVolume}
        </div>
        <div>
          <span className="titleContent">
            {`${t(BaseCardMessages.volumeDeliveredText)}`}:{" "}
          </span>
          {contractConcreteRecipe.deliveredVolume}
        </div>
        <div>
          <span className="titleContent">
            {`${t(BaseCardMessages.volumeRemainingText)}`}:{" "}
          </span>
          {balance}
        </div>
        <div>
          { showVolumePrice && (
            <>
              <span className="titleContent">
                {`${t(BaseCardMessages.volumePriceText)}`}:{" "}
              </span>
              {m3ValueFormatted}
            </>
          )}
        </div>
      </div>
    );
  }, [contractConcreteRecipe, i18n.language, t]);

  const contentInvoiceInstallment = useMemo(() => {
    if (!invoiceInstallment) return null;

    const netValueFormatted = currencyFormat(
      invoiceInstallment.netValue,
      i18n.language as TLanguages
    );

    return (
      <div>
        <div className="titleContent">
          {`${t(BaseCardMessages.installmentNumberText)}`}{" "}
          {invoiceInstallment.installmentNumber}
        </div>
        <div>
          <span className="titleContent">
            {`${t(BaseCardMessages.installmentDueDateText)}`}
            {": "}
          </span>
          {new Date(invoiceInstallment.dueDate).toLocaleDateString()}
        </div>
        <div>
          <span className="titleContent">
            {`${t(BaseCardMessages.installmentSettlementDateText)}`}
            {": "}
          </span>
          {invoiceInstallment.settlementDate
            ? new Date(invoiceInstallment.settlementDate).toLocaleDateString()
            : "Não quitado"}
        </div>
        <div>
          <span className="titleContent">
            {`${t(BaseCardMessages.installmentNetValueText)}`}
            {": "}
          </span>{" "}
          {netValueFormatted}
        </div>
      </div>
    );
  }, [invoiceInstallment, i18n.language, t]);

  const contentTravel = useMemo(() => {
    if (!travel) return null;

    const showCompleteData = travel.status === "IN_TRANSIT"
      || travel.status === "IN_CONSTRUCTION"
      || travel.status === "DISCHARGING"
      || travel.status === "DELIVERED"
      || travel.status === "INVOICE_ISSUED";

    const programmedTime = travel.scheduledTime
      && utils.formatDateIfHave(travel.scheduledTime, "timeWithoutSeconds");

    const showProgrammedTime = travel.status === "PREPARING" || travel.status === "CANCELED";
    // travel.status === "IN_TRANSIT" ||
    // travel.status === "PROGRAMMED" ||
    // travel.status === "INVOICE_ISSUED";

    const showTimeStatus = travel.status !== "CANCELED" && travel.status !== "PREPARING";

    let timeStatus;
    let timeStartStatus;

    switch (travel.status) {
      case ETravelStatus.INVOICE_ISSUED:
        if (travel.invoiceIssueTime) {
          timeStatus = utils.formatDateIfHave(
            new Date(travel.invoiceIssueTime),
            "timeWithoutSeconds"
          );
        }
        break;
      case ETravelStatus.IN_TRANSIT:
        if (travel.plantDepartureTime) {
          timeStatus = utils.formatDateIfHave(
            intervalToDuration({
              start: new Date(travel.plantDepartureTime),
              end: nowDate
            }),
            "durationDescriptiveTime"
          );
        }
        break;
      case ETravelStatus.IN_CONSTRUCTION:
        if (travel.constructionStartTime) {
          timeStatus = utils.formatDateIfHave(
            intervalToDuration({
              start: new Date(travel.constructionStartTime),
              end: nowDate
            }),
            "durationDescriptiveTime"
          );
          timeStartStatus = utils.formatDateIfHave(
            new Date(travel.constructionStartTime),
            "timeWithoutSeconds"
          );
        }
        break;
      case ETravelStatus.DISCHARGING:
        if (travel.dischargeStartTime) {
          timeStatus = utils.formatDateIfHave(
            intervalToDuration({
              start: new Date(travel.dischargeStartTime),
              end: nowDate
            }),
            "durationDescriptiveTime"
          );
          timeStartStatus = utils.formatDateIfHave(
            new Date(travel.dischargeStartTime),
            "timeWithoutSeconds"
          );
        }

        break;
      case ETravelStatus.DELIVERED:
        if (travel.plantDepartureTime) {
          const startTime = new Date(
            travel.plantDepartureTime ?? travel.invoiceIssueTime
          );
          let endTime = new Date(
            travel.invoiceIssueTime ?? travel.plantDepartureTime
          );

          if (travel.dischargeEndTime) {
            endTime = new Date(travel.dischargeEndTime);
          } else if (travel.constructionEndTime) {
            endTime = new Date(travel.constructionEndTime);
          } else if (travel.finishTime) {
            endTime = new Date(travel.finishTime);
          }

          timeStatus = utils.formatDateIfHave(
            intervalToDuration({
              start: startTime,
              end: endTime
            }),
            "durationDescriptiveTime"
          );
          timeStartStatus = utils.formatDateIfHave(
            endTime,
            "timeWithoutSeconds"
          );
        }
        break;
      case ETravelStatus.PROGRAMMED:
        if (travel.scheduledTime) {
          timeStatus = utils.formatDateIfHave(
            intervalToDuration({
              start: nowDate,
              end: new Date(travel.scheduledTime)
            }),
            "durationDescriptiveTime"
          );
        }
        break;
      default:
        break;
    }

    let prefix = "";
    let suffix = "";

    if (showTimeStatus) {
      if (travel.status !== "DELIVERED") {
        prefix = baseCardIcons.travel[travel.status]?.prefix ?? "";
      }
      if (
        travel.status === "PROGRAMMED"
        && nowDate.getTime() > new Date(travel.scheduledTime).getTime()
      ) {
        prefix = "para";
        suffix = " atrás";
      }
    }

    return (
      <div>
        {showCompleteData && (
          <div className="inlineContent">
            <div>
              <span className="titleContent">
                {`${t(BaseCardMessages.travelEquipmentText)}`}:{" "}
              </span>
              {travel.concreteMixer?.code || travel.concreteMixer?.description}
            </div>
            <div>
              <span className="titleContent">
                {`${t(BaseCardMessages.travelPlateText)}`}:{" "}
              </span>
              {travel.concreteMixer?.licensePlate}
            </div>
          </div>
        )}
        {showCompleteData && travel.driver && (
          <div>
            <span className="titleContent">
              {`${t(BaseCardMessages.travelDriverText)}`}:{" "}
            </span>
            {travel.driver.name}
          </div>
        )}
        <div>
          {showProgrammedTime ? (
            <>
              <span className="titleContent">
                {`${t(BaseCardMessages.travelProgrammedText)}`}:{" "}
              </span>
              {programmedTime}
            </>
          ) : (
            <>
              <span className="titleContent">
                {baseCardIcons.travel[travel.status].labelData}{" "}
              </span>
              {timeStartStatus}
            </>
          )}
        </div>
        {showCompleteData && (
          <div>
            <div className="inlineContent">
              <div>
                <span className="titleContent">
                  {`${t(BaseCardMessages.travelDocumentNumberText)}`}:{" "}
                </span>
                {travel.documentNumber}
              </div>
              <div>
                <span className="titleContent">
                  {`${t(BaseCardMessages.travelLoadLabelText)}`}:{" "}
                </span>
                {travel.loadLabel}
              </div>
            </div>
            <div>
              <span className="titleContent">
                {`${t(BaseCardMessages.volumeText)}: `}
              </span>
              {travel.volume}
            </div>
          </div>
        )}
        <div>
          <span className="contentColor">
            {baseCardIcons.travel[travel.status].labelStatus}
          </span>
          {showTimeStatus && ` ${prefix} ${timeStatus}${suffix}`}
        </div>
      </div>
    );
  }, [travel, t, baseCardIcons.travel, nowDate]);

  const contentConcretingRequestConcreting = useMemo(() => {
    if (!concretingRequestConcreting) return null;

    const requestedDateTimeFormatted = utils.formatDateIfHave(
      new Date(concretingRequestConcreting.requestDate),
      "timeWithoutSeconds"
    );

    return (
      <div>
        <div>
          <span className="titleContent">
            {`${t(
              BaseCardMessages.concretingRequestConcretingConcreteBatchingPlantText
            )}`}
            :{" "}
          </span>
          {concretingRequestConcreting.concreteBatchingPlantName}
        </div>
        <div>
          <span className="titleContent">
            {`${t(BaseCardMessages.concretingRequestConcretingDateText)}`}:{" "}
          </span>
          {new Date(
            concretingRequestConcreting.requestDate
          ).toLocaleDateString()}
        </div>
        <div>
          <span className="titleContent">
            {`${t(BaseCardMessages.concretingRequestConcretingTimeText)}`}:{" "}
          </span>
          {requestedDateTimeFormatted}
          &nbsp;&nbsp;&nbsp;
          <span className="titleContent">
            {`${t(BaseCardMessages.concretingRequestConcretingIntervalText)}`}:{" "}
          </span>
          {`${concretingRequestConcreting.interval} min`}
        </div>
        <div>
          <span className="titleContent">
            {`${t(BaseCardMessages.concretingRequestRequesterText)}`}:{" "}
          </span>
          {concretingRequestConcreting.requester}
          {concretingRequestConcreting.requesterPhoneNumber && (
            <span className="titleContent">
              {" - "}
            </span>
          )}
          {concretingRequestConcreting.requesterPhoneNumber}
        </div>
        {concretingRequestConcreting.observationConcreting && (
          <div>
            <span className="titleContent">
              {`${t(BaseCardMessages.concretingRequestObservationText)}`}:{" "}
            </span>
            {concretingRequestConcreting.observationConcreting}
          </div>
        )}
      </div>
    );
  }, [concretingRequestConcreting, t]);

  const contentConcretingRequestConcreteRecipe = useMemo(() => {
    if (!concretingRequestConcreteRecipe) return null;

    return (
      <div>
        <div>
          <span className="titleContent">
            {`${t(BaseCardMessages.concreteRecipeText)}`}:{" "}
          </span>
          {concretingRequestConcreteRecipe.concreteRecipeDescription}
        </div>
        <div>
          <span className="titleContent">
            {`${t(
              BaseCardMessages.concretingRequestConcreteRecipeRequestedQuantityText
            )}`}
            :{" "}
          </span>
          {`${concretingRequestConcreteRecipe.requestedQuantity} m³`}
        </div>
        <div>
          <span className="titleContent">
            {`${t(
              BaseCardMessages.concretingRequestConcreteRecipeQuantityPerConcreteMixerText
            )}`}
            :{" "}
          </span>{" "}
          {`${concretingRequestConcreteRecipe.quantityPerConcreteMixer} m³`}
        </div>
        {concretingRequestConcreteRecipe.pieceToBeConcreted && (
          <div>
            <span className="titleContent">
              {`${t(
                BaseCardMessages.concretingRequestConcreteRecipePieceToBeConcretedText
              )}`}
              :{" "}
            </span>{" "}
            {concretingRequestConcreteRecipe.pieceToBeConcreted}
          </div>
        )}
      </div>
    );
  }, [concretingRequestConcreteRecipe, t]);

  const contentConcretingRequestPump = useMemo(() => {
    if (!concretingRequestPump) return null;

    const pump = pumpOptions.find(
      (pumpOption) => pumpOption.id === concretingRequestPump.pump
    );

    return (
      <div>
        <div>
          <span className="titleContent">
            {`${t(BaseCardMessages.concretingRequestPumpDescriptionText)}`}:{" "}
          </span>
          {pump?.value}
        </div>
        <div>
          <span className="titleContent">
            {`${t(BaseCardMessages.concretingRequestPipingDistanceText)}`}:{" "}
          </span>
          {`${concretingRequestPump.pipingDistance} m`}
        </div>
        {concretingRequestPump.observationPump && (
          <div>
            <span className="titleContent">
              {`${t(BaseCardMessages.concretingRequestObservationText)}`}:{" "}
            </span>
            {concretingRequestPump.observationPump}
          </div>
        )}
      </div>
    );
  }, [concretingRequestPump, t]);

  const contentUser = useMemo(() => {
    if (!user) return null;

    return (
      <div>
        { user.document && (
          <div>
            <span className="titleContent">
              {`${t(SignUpMessages.signUpDocumentLabel)}: `}
            </span>
            {user?.document}
          </div>
        )}
        <div>
          <span className="titleContent">
            {`${t(SignUpMessages.signUpPhoneNumberLabel)}: `}
          </span>
          {user.phoneNumber}
        </div>
        { user.clientName && (
          <div>
            <span className="titleContent">
              {`${t(SignUpMessages.signUpClientName)}: `}
            </span>
            {user.clientName}
          </div>
        )}
      </div>
    );
  }, [user, t]);

  const contentConstructor = useMemo(() => {
    if (type === "contractConcreteRecipe") {
      return contentContractConcreteRecipe;
    }

    if (type === "invoiceInstallment") {
      return contentInvoiceInstallment;
    }

    if (type === "concretingRequestConcreting") {
      return contentConcretingRequestConcreting;
    }

    if (type === "concretingRequestConcreteRecipe") {
      return contentConcretingRequestConcreteRecipe;
    }

    if (type === "concretingRequestPump") {
      return contentConcretingRequestPump;
    }

    if (type === "user") {
      return contentUser;
    }

    return contentTravel;
  }, [
    type,
    contentTravel,
    contentContractConcreteRecipe,
    contentInvoiceInstallment,
    contentConcretingRequestConcreting,
    contentConcretingRequestConcreteRecipe,
    contentConcretingRequestPump,
    contentUser
  ]);

  const action = useMemo(() => {
    if (type === "contractConcreteRecipe") return null;
    if (
      type === "travel"
      && ["DISCHARGING", "IN_CONSTRUCTION", "IN_TRANSIT", "DELIVERED"].includes(
        travel?.status ?? ("" as ETravelStatus)
      )
    ) {
      return <ArrowForwardIos />;
    }

    if (type === "invoiceInstallment") return <ReactSVG src={Barcode} />;

    return null;
  }, [type, travel?.status]);

  const contentCard = useMemo(() => (
    <>
      <Styled.Content className={type === "user" ? "userType" : ""}>
        <Styled.ContentContainer>
          <Styled.IconContainer
            color={baseCardIcons[type][objectInUse.status ?? ""].color}
          >
            <ReactSVG src={baseCardIcons[type][objectInUse.status ?? ""].icon} />
          </Styled.IconContainer>

          <Styled.InfoContainer
            color={baseCardIcons[type][objectInUse.status ?? ""].color}
          >
            {contentConstructor}
          </Styled.InfoContainer>
        </Styled.ContentContainer>
        {type === "user" && (
          <button type="button" aria-label="Delete" onClick={() => { setIsDialogOpen(true); }}>
            <ReactSVG
              src={user?.active ? InactivateUser : ActivateUser}
              style={user?.active ? { width: 25, height: 25 } : { width: 21, height: 21 }}
            />
          </button>
        )}
      </Styled.Content>
      <InactiveUserDialog open={isDialogOpen} onClose={() => { setIsDialogOpen(false); }} user={user} />
    </>
  ), [baseCardIcons, type, objectInUse.status, contentConstructor, isDialogOpen]);

  useEffect(() => {
    const interval = setInterval(() => {
      setNowDate(new Date());
    }, 30000);

    return () => {
      clearInterval(interval);
    };
  }, []);

  return (
    <Styled.Container
      shadowInset={shadowInset}
      id={cardItemContainerId}
      onClick={handleClick}
      className={hasAction ? "action" : ""}
    >
      {contentCard}

      <Styled.ActionContainer
        id={`${cardItemContainerId}_action`}
        color={
          type === "invoiceInstallment"
            ? baseCardIcons[type][objectInUse.status ?? "p"].color
            : undefined
        }
      >
        {action}
      </Styled.ActionContainer>
      {type === "travel" && travel && travelDialogOpen && (
        <TravelDialog
          open={travelDialogOpen}
          handleClose={() => {
            handleTravelDialog(false);
          }}
          travel={travel}
          header={contentCard}
          concretingId={concretingId}
        />
      )}
    </Styled.Container>
  );
};

BaseCardItem.defaultProps = {
  contractConcreteRecipe: undefined,
  travel: undefined,
  invoiceInstallment: undefined,
  concretingRequestConcreting: undefined,
  concretingRequestConcreteRecipe: undefined,
  concretingRequestPump: undefined,
  user: undefined,
  shadowInset: true,
  controlDialog: undefined,
  concretingId: undefined,
  showVolumePrice: false
};

export default BaseCardItem;
